import React, { createContext, useContext, useEffect, useState } from 'react'
import produce from 'immer'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import useReasonPolicyRule from 'src/core/hooks/use-reason-policy-rule'

import { Segments } from 'src/components/SegmentControl'

import Advances from './components/Advances'
import Air from './components/Air'
import Vehicles from './components/vehicles'
import Hotels from './components/Hotels'

interface Params {
  reason_id: string
}

export type SegmentKeys = 'advances' | 'air' | 'hotels' | 'vehicles'

export type ServicesSegments = Segments<SegmentKeys>

interface SegmentControlContextValues {
  current: SegmentKeys
  segments: ServicesSegments
  setCurrent(_: SegmentKeys): void
  setSegments(_: ServicesSegments): void
  setDisabled(_: SegmentKeys, v: boolean): void
}

const segments: ServicesSegments = {
  advances: {
    disabled: false,
    title: 'web.services.advances.title',
    renderContent: () => <Advances />
  },
  air: {
    disabled: false,
    title: 'web.services.airs.title',
    renderContent: () => <Air />
  },
  hotels: {
    disabled: false,
    title: 'web.services.hotels.title',
    renderContent: () => <Hotels />
  },
  vehicles: {
    disabled: false,
    title: 'web.services.vehicle.title',
    renderContent: () => <Vehicles />
  }
}

const initialValues: SegmentControlContextValues = {
  current: 'advances',
  segments,
  setCurrent(_: SegmentKeys) {},
  setSegments(_: ServicesSegments) {},
  setDisabled(_: SegmentKeys, v: boolean) {}
}

export const SegmentControlContext =
  createContext<SegmentControlContextValues>(initialValues)

export function useSegmentControlContext(): SegmentControlContextValues {
  return useContext(SegmentControlContext)
}

function Services({
  match: {
    params: { reason_id }
  }
}: RouteComponentProps<Params>) {
  const [current, setCurrent] = useState<SegmentKeys>(initialValues.current)
  const [segments, setSegments] = useState<ServicesSegments>()

  const policyRule = useReasonPolicyRule(reason_id)

  useEffect(() => {
    const nextSegments = { ...initialValues.segments }

    if (policyRule) {
      if (!policyRule.enable_advance) {
        delete nextSegments.advances
      }

      if (!policyRule.enable_flight) {
        delete nextSegments.air
      }

      if (!policyRule.enable_hotel) {
        delete nextSegments.hotels
      }

      if (!policyRule.enable_vehicle_rent) {
        delete nextSegments.vehicles
      }

      if (!nextSegments.advances) {
        const keys = Object.keys(nextSegments)

        // @ts-ignore
        setCurrent(keys[0])
      }
    }

    setSegments({ ...nextSegments })
  }, [policyRule])

  function setDisabled(key: SegmentKeys, value: boolean) {
    setSegments(
      produce(segments, draft => {
        if (!!draft[key]) draft[key].disabled = value
      })
    )
  }

  return (
    <SegmentControlContext.Provider
      value={{
        current,
        segments,
        setCurrent,
        setSegments,
        setDisabled
      }}
    >
      {!!segments && !!segments[current] && segments[current].renderContent()}
    </SegmentControlContext.Provider>
  )
}

export default withRouter(Services)
